import { DocumentTypeCode, DocumentTypeCodeDescription } from '@quromedical/fhir-common';
import {
  DocumentClinicalReferral,
  DocumentHomeBasedCareReferral,
  DocumentMotivationMedicalDevice,
} from '@quromedical/models';
import { Field, SelectOption } from 'components/types';
import { MedicationFetcher } from 'core/medication';
import { medicationFetcher } from 'integration';
import { strings } from 'strings';

type FormData = {
  medications: SelectOption<any, string>;
  quantity: string;
  notes?: string;
};

export const mapMedicationsScript = (formItems: DocumentForm[], notes?: string) => {
  const test = formItems.map(m => {
    return {
      nappi: m.medications.value,
      name: m.medications.display,
      qty: m.quantity,
    };
  });

  return {
    medications: test,
    notes,
  };
};

export const documentTypeFetcher = async (): Promise<
  SelectOption<undefined, DocumentTypeCode>[]
> => [
  {
    display: DocumentTypeCodeDescription[DocumentTypeCode.clinicalReferral],
    value: DocumentTypeCode.clinicalReferral,
  },
  {
    display: DocumentTypeCodeDescription[DocumentTypeCode.homeBasedCareReferral],
    value: DocumentTypeCode.homeBasedCareReferral,
  },
  {
    display: DocumentTypeCodeDescription[DocumentTypeCode.motivationMedicalDevice],
    value: DocumentTypeCode.motivationMedicalDevice,
  },
  {
    display: DocumentTypeCodeDescription[DocumentTypeCode.createScript],
    value: DocumentTypeCode.createScript,
  },
];

export type DocumentForm = DocumentMotivationMedicalDevice &
  FormData &
  DocumentClinicalReferral &
  DocumentHomeBasedCareReferral & {
    type: DocumentTypeCode;
  };

const motivationMedicalDeviceFields: Field<Partial<DocumentForm>>[] = [
  {
    heading: strings.DocumentMotivationMedicalDevice,
    subfields: [
      {
        key: 'deviceName',
        label: strings.DocumentDeviceName,
        type: 'text-box',
      },
    ],
  },
];

export const medicationNotesFields: Field<Partial<FormData>>[] = [
  {
    subfields: [
      {
        type: 'text-box',
        key: 'notes',
        multiline: true,
        numberOfLines: 5,
        label: strings.LabelMedicationNoteText,
      },
    ],
  },
];

const clinicalReferralFields: Field<Partial<DocumentForm>>[] = [
  {
    subfields: [
      {
        key: 'reasonForReferral',
        label: strings.DocumentReasonForReferral,
        type: 'text-box',
        multiline: true,
      },
      {
        key: 'medicalHistory',
        label: strings.DocumentMedicalHistory,
        type: 'text-box',
        multiline: true,
      },
    ],
  },
  {
    subfields: [
      {
        key: 'currentTreatment',
        label: strings.DocumentCurrentTreatment,
        type: 'text-box',
        multiline: true,
      },
      {
        key: 'recentInvestigations',
        label: strings.DocumentRecentInvestigations,
        type: 'text-box',
        multiline: true,
      },
    ],
  },
  {
    subfields: [
      {
        key: 'externalPractitionerName',
        label: strings.DocumentExternalPractitionerName,
        type: 'text-box',
      },
      {
        key: 'externalPractitionerDiscipline',
        label: strings.DocumentExternalPractitionerDiscipline,
        type: 'text-box',
      },
    ],
  },
  {
    subfields: [
      {
        key: 'externalPractitionerPhoneNumber',
        label: strings.DocumentExternalPractitionerPhone,
        type: 'text-box',
      },
      {
        key: 'externalPractitionerEmail',
        label: strings.DocumentExternalPractitionerEmail,
        type: 'text-box',
      },
    ],
  },
];

export const createMedicationUsageFields = (
  medicationFetcher: MedicationFetcher
): Field<Partial<FormData>>[] => [
  {
    subfields: [
      {
        key: 'medications',
        label: strings.LabelMedication,
        initialSelection: [],
        searchable: true,
        type: 'combobox-single',
        fetcher: medicationFetcher,
        returnFullOption: true,
      },
      {
        key: 'quantity',
        type: 'text-box',
        label: strings.LabelConsumableQuantity,
      },
    ],
  },
];
const assignableMedicationFetcher = medicationFetcher('create-usage');
const createMedicationFields = createMedicationUsageFields(assignableMedicationFetcher);

const documentTypeDataFields: Record<DocumentTypeCode, Field<Partial<DocumentForm>>[]> = {
  [DocumentTypeCode.motivationMedicalDevice]: motivationMedicalDeviceFields,
  [DocumentTypeCode.homeBasedCareReferral]: clinicalReferralFields,
  [DocumentTypeCode.clinicalReferral]: clinicalReferralFields,
  [DocumentTypeCode.createScript]: createMedicationFields,
};
export const documentFields: Field<Partial<DocumentForm>>[] = [
  {
    icon: 'edit-document',
    heading: 'create Document',
    subfields: [
      {
        key: 'type',
        fetcher: documentTypeFetcher,
        label: 'Document Type',
        type: 'combobox-single',
      },
    ],
  },
];

export const createDocumentFields = (
  selectedType: DocumentTypeCode | undefined
): Field<Partial<DocumentForm>>[] => {
  const dataFields = selectedType ? documentTypeDataFields[selectedType] : [];

  const documentField: Field<Partial<DocumentForm>>[] = [...(dataFields ? dataFields : [])];

  return documentField;
};
